import { useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@repo/backend';
import type { Id } from '../../../../packages/backend/convex/_generated/dataModel';

/** Stable anonymous session ID persisted for the browser tab's lifetime. */
function getSessionId(): string {
  const KEY = 'tgc_session_id';
  let id = sessionStorage.getItem(KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(KEY, id);
  }
  return id;
}

/**
 * Returns the real-time viewer count for an auction page.
 *
 * - Sends a heartbeat to Convex on mount and every 30 s so the current
 *   visitor is counted for up to 60 s after their last heartbeat.
 * - The count is a live Convex query that re-renders whenever it changes.
 */
export function useLiveViewerCount(auctionId: string): number {
  const count = useQuery(api.presence.queries.viewerCount, {
    auctionId: auctionId as Id<'auctions'>,
  });

  const heartbeat = useMutation(api.presence.mutations.heartbeat);

  useEffect(() => {
    const sessionId = getSessionId();
    const id = auctionId as Id<'auctions'>;

    // Register immediately on mount
    void heartbeat({ auctionId: id, sessionId });

    // Keep alive every 30 s (half the 60 s active threshold)
    const timer = setInterval(() => {
      void heartbeat({ auctionId: id, sessionId });
    }, 30_000);

    return () => clearInterval(timer);
  }, [auctionId, heartbeat]);

  return count ?? 0;
}
